import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getEventById, updateEventPressData } from '../../../../api';
import Spinner from '../../../Atoms/Spinner';
import toast from 'react-hot-toast';

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Header = styled.div`
    margin-bottom: 40px;
    
    h1 {
        font-size: 32px;
        margin-bottom: 8px;
        color: #333;
    }
    
    .subtitle {
        font-size: 18px;
        color: #666;
    }
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    margin-bottom: 20px;
    
    &:hover {
        background: #e8e8e8;
        transform: translateX(-4px);
    }
`;

const FormSection = styled.div`
    background: white;
    padding: 40px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    margin-bottom: 40px;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 24px;
    
    label {
        display: block;
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 12px;
    }
    
    input[type="text"],
    input[type="password"] {
        width: 100%;
        padding: 12px 16px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        font-size: 16px;
        transition: all 0.2s ease;
        
        &:focus {
            outline: none;
            border-color: #9b59b6;
        }
    }
`;

const FileUpload = styled.div`
    border: 2px dashed #e0e0e0;
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    
    &:hover {
        border-color: #9b59b6;
        background: #f8f4fa;
    }
    
    input[type="file"] {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        opacity: 0;
        cursor: pointer;
    }
    
    .upload-icon {
        font-size: 48px;
        margin-bottom: 12px;
        color: #9b59b6;
    }
    
    .upload-text {
        font-size: 14px;
        color: #666;
        margin-bottom: 8px;
    }
    
    .file-name {
        font-size: 14px;
        color: #333;
        font-weight: 500;
        margin-top: 12px;
    }
`;

const PreviewSection = styled.div`
    background: #f8f8f8;
    padding: 40px;
    border-radius: 16px;
    margin-bottom: 40px;
    
    h2 {
        font-size: 24px;
        margin-bottom: 24px;
        color: #333;
    }
    
    .preview-url {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
        background: white;
        border-radius: 8px;
        margin-bottom: 16px;
        
        input {
            flex: 1;
            border: none;
            background: none;
            font-size: 14px;
            color: #666;
        }
        
        button {
            padding: 8px 16px;
            background: #9b59b6;
            color: white;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s ease;
            
            &:hover {
                background: #8e44ad;
            }
        }
    }
`;

const SaveButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 14px 32px;
    background: linear-gradient(135deg, #9b59b6, #8e44ad);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(155, 89, 182, 0.3);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const PreviewButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 14px 32px;
    background: white;
    color: #9b59b6;
    border: 2px solid #9b59b6;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-left: 16px;

    &:hover {
        background: #f8f4fa;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(155, 89, 182, 0.2);
    }
`;

const RemoveButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: #fee;
    color: #e74c3c;
    border: 1px solid #e74c3c;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 12px;

    &:hover {
        background: #fcc;
        transform: translateY(-1px);
    }
`;

const FD100PressPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const eventId = searchParams.get('id');
    
    const [loading, setLoading] = useState(true);
    const [event, setEvent] = useState(null);
    const [saving, setSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        accessCode: '',
        backgroundImage: null,
        keyVisual: null,
        pressRelease: null,
        pressKit: null,
        fourAngles: null
    });

    const [fileNames, setFileNames] = useState({
        backgroundImage: '',
        keyVisual: '',
        pressRelease: '',
        pressKit: '',
        fourAngles: ''
    });

    const [customDocuments, setCustomDocuments] = useState([]);
    const [newDocTitle, setNewDocTitle] = useState('');
    const [newDocFile, setNewDocFile] = useState(null);
    const [newDocFileName, setNewDocFileName] = useState('');

    useEffect(() => {
        fetchEvent();
    }, [eventId]);

    const fetchEvent = async () => {
        try {
            const response = await getEventById({id: eventId});
            setEvent(response.data);
            
            // Load existing press data if available
            if (response.data.pressPage) {
                setFormData({
                    ...formData,
                    accessCode: response.data.pressPage.accessCode || ''
                });

                // Set existing file names
                const pressPage = response.data.pressPage;
                setFileNames({
                    backgroundImage: pressPage.backgroundImageName || '',
                    keyVisual: pressPage.keyVisualName || '',
                    pressRelease: pressPage.pressReleaseName || '',
                    pressKit: pressPage.pressKitName || '',
                    fourAngles: pressPage.fourAnglesName || ''
                });

                // Load custom documents
                if (pressPage.customDocuments) {
                    setCustomDocuments(pressPage.customDocuments);
                }
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error fetching event:', error);
            toast.error('Erreur lors du chargement de l\'événement');
            setLoading(false);
        }
    };

    const handleFileChange = (e, fileType) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({
                ...formData,
                [fileType]: file
            });
            setFileNames({
                ...fileNames,
                [fileType]: file.name
            });
        }
    };

    const handleSave = async () => {
        if (!formData.accessCode) {
            toast.error('Veuillez définir un code d\'accès');
            return;
        }
        
        setSaving(true);
        
        try {
            const data = new FormData();
            data.append('accessCode', formData.accessCode);

            if (formData.backgroundImage) data.append('backgroundImage', formData.backgroundImage);
            if (formData.keyVisual) data.append('keyVisual', formData.keyVisual);
            if (formData.pressRelease) data.append('pressRelease', formData.pressRelease);
            if (formData.pressKit) data.append('pressKit', formData.pressKit);
            if (formData.fourAngles) data.append('fourAngles', formData.fourAngles);

            await updateEventPressData(eventId, data);
            toast.success('Page presse mise à jour avec succès');

        } catch (error) {
            console.error('Error saving press data:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const copyToClipboard = () => {
        const url = `${window.location.origin}/presse/fd100/${eventId}`;
        navigator.clipboard.writeText(url);
        toast.success('URL copiée dans le presse-papier');
    };

    const handlePreview = () => {
        const url = `${window.location.origin}/presse/fd100/${eventId}`;
        window.open(url, '_blank');
    };

    const handleRemoveBackground = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer l\'image de fond ?')) {
            return;
        }

        setSaving(true);
        try {
            const data = new FormData();
            data.append('accessCode', formData.accessCode);
            data.append('removeBackgroundImage', 'true');

            await updateEventPressData(eventId, data);

            // Clear local state
            setFormData({ ...formData, backgroundImage: null });
            setFileNames({ ...fileNames, backgroundImage: '' });

            toast.success('Image de fond supprimée');

            // Refresh event data
            await fetchEvent();
        } catch (error) {
            console.error('Error removing background:', error);
            toast.error('Erreur lors de la suppression');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveDocument = async (documentType, documentLabel) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${documentLabel} ?`)) {
            return;
        }

        setSaving(true);
        try {
            const data = new FormData();
            data.append('accessCode', formData.accessCode);
            data.append(`remove${documentType.charAt(0).toUpperCase() + documentType.slice(1)}`, 'true');

            await updateEventPressData(eventId, data);

            // Clear local state
            setFormData({ ...formData, [documentType]: null });
            setFileNames({ ...fileNames, [documentType]: '' });

            toast.success(`${documentLabel} supprimé(e)`);

            // Refresh event data
            await fetchEvent();
        } catch (error) {
            console.error('Error removing document:', error);
            toast.error('Erreur lors de la suppression');
        } finally {
            setSaving(false);
        }
    };

    const handleAddCustomDocument = async () => {
        if (!newDocTitle || !newDocFile) {
            toast.error('Veuillez renseigner un titre et sélectionner un fichier');
            return;
        }

        setSaving(true);
        try {
            const data = new FormData();
            data.append('accessCode', formData.accessCode);
            data.append('customDocumentTitle', newDocTitle);
            data.append('customDocument', newDocFile);

            await updateEventPressData(eventId, data);

            // Clear form
            setNewDocTitle('');
            setNewDocFile(null);
            setNewDocFileName('');

            toast.success('Document ajouté avec succès');

            // Refresh event data
            await fetchEvent();
        } catch (error) {
            console.error('Error adding custom document:', error);
            toast.error('Erreur lors de l\'ajout du document');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveCustomDocument = async (docId, docTitle) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${docTitle}" ?`)) {
            return;
        }

        setSaving(true);
        try {
            const data = new FormData();
            data.append('accessCode', formData.accessCode);
            data.append('removeCustomDocumentId', docId);

            await updateEventPressData(eventId, data);

            toast.success('Document supprimé');

            // Refresh event data
            await fetchEvent();
        } catch (error) {
            console.error('Error removing custom document:', error);
            toast.error('Erreur lors de la suppression');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Spinner />;
    if (!event) return <div>Événement non trouvé</div>;

    const pressPageUrl = `${window.location.origin}/presse/fd100/${eventId}`;

    return (
        <Container>
            <BackButton onClick={() => navigate('/admin/events')}>
                ← Retour aux événements
            </BackButton>
            
            <Header>
                <h1>Page Presse - {event.title_fr}</h1>
                <div className="subtitle">Gérez la page presse pour cet événement FD100</div>
            </Header>

            <FormSection>
                <h2>Configuration de la page</h2>
                
                <FormGroup>
                    <label>Code d'accès</label>
                    <input
                        type="password"
                        placeholder="Définir un code d'accès pour la page presse"
                        value={formData.accessCode}
                        onChange={(e) => setFormData({ ...formData, accessCode: e.target.value })}
                    />
                </FormGroup>

                <h3 style={{ marginTop: '32px', marginBottom: '24px' }}>Personnalisation</h3>

                <FormGroup>
                    <label>Image de fond</label>
                    <div style={{
                        fontSize: '14px',
                        color: '#666',
                        marginBottom: '12px',
                        padding: '12px',
                        background: '#f0f9ff',
                        borderRadius: '8px',
                        borderLeft: '4px solid #3498db'
                    }}>
                        💡 <strong>Résolution recommandée :</strong> 1920×1080 pixels (Full HD) ou supérieure
                        <br />
                        <span style={{ fontSize: '13px', color: '#888', marginTop: '4px', display: 'block' }}>
                            Format : JPG, PNG ou WebP • Poids optimal : moins de 2 MB
                        </span>
                    </div>
                    <FileUpload>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'backgroundImage')}
                        />
                        <div className="upload-icon">🎨</div>
                        <div className="upload-text">Image de fond personnalisée pour la page presse</div>
                        {fileNames.backgroundImage && (
                            <div className="file-name">{fileNames.backgroundImage}</div>
                        )}
                    </FileUpload>
                    {fileNames.backgroundImage && (
                        <RemoveButton onClick={handleRemoveBackground} disabled={saving}>
                            🗑️ Supprimer l'image de fond
                        </RemoveButton>
                    )}
                </FormGroup>

                <h3 style={{ marginTop: '32px', marginBottom: '24px' }}>Documents à télécharger</h3>
                
                <FormGrid>
                    <FormGroup>
                        <label>Key Visual</label>
                        <FileUpload>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, 'keyVisual')}
                            />
                            <div className="upload-icon">🖼️</div>
                            <div className="upload-text">Cliquez ou glissez pour télécharger</div>
                            {fileNames.keyVisual && (
                                <div className="file-name">{fileNames.keyVisual}</div>
                            )}
                        </FileUpload>
                        {fileNames.keyVisual && (
                            <RemoveButton onClick={() => handleRemoveDocument('keyVisual', 'le Key Visual')} disabled={saving}>
                                🗑️ Supprimer
                            </RemoveButton>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <label>Communiqué de presse</label>
                        <FileUpload>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleFileChange(e, 'pressRelease')}
                            />
                            <div className="upload-icon">📄</div>
                            <div className="upload-text">Cliquez ou glissez pour télécharger</div>
                            {fileNames.pressRelease && (
                                <div className="file-name">{fileNames.pressRelease}</div>
                            )}
                        </FileUpload>
                        {fileNames.pressRelease && (
                            <RemoveButton onClick={() => handleRemoveDocument('pressRelease', 'le Communiqué de presse')} disabled={saving}>
                                🗑️ Supprimer
                            </RemoveButton>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <label>Dossier de presse</label>
                        <FileUpload>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleFileChange(e, 'pressKit')}
                            />
                            <div className="upload-icon">📋</div>
                            <div className="upload-text">Cliquez ou glissez pour télécharger</div>
                            {fileNames.pressKit && (
                                <div className="file-name">{fileNames.pressKit}</div>
                            )}
                        </FileUpload>
                        {fileNames.pressKit && (
                            <RemoveButton onClick={() => handleRemoveDocument('pressKit', 'le Dossier de presse')} disabled={saving}>
                                🗑️ Supprimer
                            </RemoveButton>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <label>Document "4 angles"</label>
                        <FileUpload>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleFileChange(e, 'fourAngles')}
                            />
                            <div className="upload-icon">📐</div>
                            <div className="upload-text">Cliquez ou glissez pour télécharger</div>
                            {fileNames.fourAngles && (
                                <div className="file-name">{fileNames.fourAngles}</div>
                            )}
                        </FileUpload>
                        {fileNames.fourAngles && (
                            <RemoveButton onClick={() => handleRemoveDocument('fourAngles', 'le Document "4 angles"')} disabled={saving}>
                                🗑️ Supprimer
                            </RemoveButton>
                        )}
                    </FormGroup>
                </FormGrid>

                <h3 style={{ marginTop: '48px', marginBottom: '24px' }}>Documents personnalisés</h3>

                {/* List existing custom documents */}
                {customDocuments && customDocuments.length > 0 && (
                    <div style={{ marginBottom: '32px' }}>
                        {customDocuments.map((doc) => (
                            <div key={doc._id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px',
                                background: '#f8f8f8',
                                borderRadius: '8px',
                                marginBottom: '12px'
                            }}>
                                <div>
                                    <strong>{doc.title}</strong>
                                    <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                                        {doc.fileName}
                                    </div>
                                </div>
                                <RemoveButton onClick={() => handleRemoveCustomDocument(doc._id, doc.title)} disabled={saving}>
                                    🗑️ Supprimer
                                </RemoveButton>
                            </div>
                        ))}
                    </div>
                )}

                {/* Add new custom document */}
                <div style={{ background: '#f0f9ff', padding: '24px', borderRadius: '12px', marginBottom: '32px' }}>
                    <h4 style={{ marginBottom: '16px' }}>Ajouter un nouveau document</h4>

                    <FormGroup>
                        <label>Titre du document</label>
                        <input
                            type="text"
                            placeholder="Ex: Photos haute résolution, Logo, etc."
                            value={newDocTitle}
                            onChange={(e) => setNewDocTitle(e.target.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <label>Fichier</label>
                        <FileUpload>
                            <input
                                type="file"
                                accept="image/*,.pdf,.doc,.docx"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setNewDocFile(file);
                                        setNewDocFileName(file.name);
                                    }
                                }}
                            />
                            <div className="upload-icon">📎</div>
                            <div className="upload-text">Images, PDF ou documents</div>
                            {newDocFileName && (
                                <div className="file-name">{newDocFileName}</div>
                            )}
                        </FileUpload>
                    </FormGroup>

                    <SaveButton onClick={handleAddCustomDocument} disabled={saving || !newDocTitle || !newDocFile}>
                        ➕ Ajouter le document
                    </SaveButton>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <SaveButton onClick={handleSave} disabled={saving}>
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </SaveButton>
                    {formData.accessCode && (
                        <PreviewButton onClick={handlePreview}>
                            👁️ Voir la page
                        </PreviewButton>
                    )}
                </div>
            </FormSection>

            {formData.accessCode && (
                <PreviewSection>
                    <h2>Aperçu et partage</h2>
                    <div className="preview-url">
                        <input type="text" value={pressPageUrl} readOnly />
                        <button onClick={copyToClipboard}>Copier l'URL</button>
                    </div>
                    <p>Code d'accès: {formData.accessCode}</p>
                </PreviewSection>
            )}
        </Container>
    );
};

export default FD100PressPage;